/**
 * Simula dedo en la preview móvil (?frame=1):
 * - cursor círculo negro
 * - mouse → TouchEvents (carruseles)
 * - arrastre en contenedores overflow-x (swipers / strips)
 */

function findScrollableX(el) {
  let node = el;
  while (node && node !== document.body && node !== document.documentElement) {
    if (node.nodeType === 1) {
      const style = window.getComputedStyle(node);
      const ox = style.overflowX;
      if (
        (ox === "auto" || ox === "scroll" || ox === "overlay") &&
        node.scrollWidth > node.clientWidth + 2
      ) {
        return node;
      }
      // Strips conocidos de la demo
      if (
        node.classList &&
        (node.classList.contains("storefront-collections__strip") ||
          node.classList.contains("carousel-inner") ||
          node.classList.contains("storefront-nav__track") ||
          node.classList.contains("storefront-nav--mobile"))
      ) {
        if (node.scrollWidth > node.clientWidth + 2) return node;
      }
    }
    node = node.parentElement;
  }
  return null;
}

function canUseTouchApi() {
  try {
    return typeof TouchEvent !== "undefined" && typeof Touch !== "undefined";
  } catch (e) {
    return false;
  }
}

function makeTouch(target, e, id) {
  return new Touch({
    identifier: id,
    target: target || e.target,
    clientX: e.clientX,
    clientY: e.clientY,
    pageX: e.pageX,
    pageY: e.pageY,
    screenX: e.screenX,
    screenY: e.screenY,
    radiusX: 14,
    radiusY: 14,
    rotationAngle: 0,
    force: 1,
  });
}

function dispatchTouch(type, target, e, touches, changed) {
  if (!canUseTouchApi() || !target) return;
  try {
    const evt = new TouchEvent(type, {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window,
      touches: touches,
      targetTouches: touches,
      changedTouches: changed,
    });
    target.dispatchEvent(evt);
  } catch (err) {
    // Algunos navegadores rechazan TouchEvent sintético; el drag-scroll cubre strips.
  }
}

/**
 * @param {object} opts
 * @param {(p:{x:number,y:number,down:boolean,visible:boolean})=>void} opts.onFinger
 * @returns {() => void} teardown
 */
export function installMobileTouchSimulation(opts = {}) {
  const onFinger = typeof opts.onFinger === "function" ? opts.onFinger : () => {};
  const state = {
    down: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    target: null,
    scrollEl: null,
    scrollLeft0: 0,
    moved: false,
    touchId: 1,
  };
  let suppressClick = false;

  const updateFinger = (e, visible = true) => {
    onFinger({
      x: e.clientX,
      y: e.clientY,
      down: state.down,
      visible,
    });
  };

  const onMove = (e) => {
    updateFinger(e, true);
    if (!state.down) return;

    state.lastX = e.clientX;
    state.lastY = e.clientY;

    if (Math.abs(e.clientX - state.startX) + Math.abs(e.clientY - state.startY) > 6) {
      state.moved = true;
    }

    if (state.scrollEl) {
      state.scrollEl.scrollLeft = state.scrollLeft0 - (e.clientX - state.startX);
      e.preventDefault();
    }

    if (canUseTouchApi() && state.target) {
      const t = makeTouch(state.target, e, state.touchId);
      dispatchTouch("touchmove", state.target, e, [t], [t]);
    }

    // Evitar selección de texto al arrastrar
    if (state.moved) e.preventDefault();
  };

  const onUp = (e) => {
    if (!state.down) {
      updateFinger(e, true);
      return;
    }
    if (canUseTouchApi() && state.target) {
      const t = makeTouch(state.target, e, state.touchId);
      dispatchTouch("touchend", state.target, e, [], [t]);
    }

    // Carrusel Bootstrap: si el swipe táctil no corre, avanzar/retroceder por umbral
    if (state.moved && state.target) {
      const dx = e.clientX - state.startX;
      const carousel =
        state.target.closest && state.target.closest(".carousel");
      if (carousel && Math.abs(dx) > 48 && !state.scrollEl) {
        const sel =
          dx < 0 ? ".carousel-control-next" : ".carousel-control-prev";
        const btn = carousel.querySelector(sel);
        if (btn) btn.click();
      }
    }

    if (state.moved && state.scrollEl) {
      suppressClick = true;
    }

    state.down = false;
    state.target = null;
    state.scrollEl = null;
    updateFinger(e, true);
  };

  const onDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    // No interferir con inputs / botones de UI del layout admin (no deberían existir en frame)
    const tag = (e.target && e.target.tagName) || "";
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

    state.down = true;
    state.moved = false;
    state.startX = e.clientX;
    state.startY = e.clientY;
    state.lastX = e.clientX;
    state.lastY = e.clientY;
    state.target = e.target;
    state.scrollEl = findScrollableX(e.target);
    state.scrollLeft0 = state.scrollEl ? state.scrollEl.scrollLeft : 0;

    if (canUseTouchApi() && state.target) {
      const t = makeTouch(state.target, e, state.touchId);
      dispatchTouch("touchstart", state.target, e, [t], [t]);
    }
    updateFinger(e, true);
  };

  const onLeave = () => {
    onFinger({ x: 0, y: 0, down: false, visible: false });
  };

  const onClickCapture = (e) => {
    if (!suppressClick) return;
    suppressClick = false;
    e.preventDefault();
    e.stopPropagation();
  };

  document.body.classList.add("storefront-touch-sim");
  document.addEventListener("mousedown", onDown, true);
  document.addEventListener("mousemove", onMove, true);
  document.addEventListener("mouseup", onUp, true);
  document.addEventListener("mouseleave", onLeave, true);
  document.addEventListener("click", onClickCapture, true);

  return function teardown() {
    document.body.classList.remove("storefront-touch-sim");
    document.removeEventListener("mousedown", onDown, true);
    document.removeEventListener("mousemove", onMove, true);
    document.removeEventListener("mouseup", onUp, true);
    document.removeEventListener("mouseleave", onLeave, true);
    document.removeEventListener("click", onClickCapture, true);
    onFinger({ x: 0, y: 0, down: false, visible: false });
  };
}
